import {
  getDatetimePresets,
  NEVER_LAST_ACTIVITY_PRESET_LABEL,
  NEVER_LAST_ACTIVITY_PRESET_VALUE,
} from "@/lib/utils/formatting/datetime-presets";
import type { SimpleFieldConfig, SavedViewConfig } from "../../types";
import {
  isCompanyFilterField,
  isContactFilterField,
  isUserFilterField,
  looksLikeUuid,
} from "../advanced-filters/utils/field-kind";
import { getFilterTypeLabel } from "../advanced-filters/utils/operator-labels";

/**
 * Resolves a raw scalar id (app_users/companies/contacts) to a human name.
 * Supplied by the rendering component (which has the lookup caches); pure
 * callers omit it and rely on the UUID mask below.
 */
export type FilterNameResolver = (
  fieldConfig: SimpleFieldConfig | undefined,
  value: string
) => string | undefined;

/** Friendly label for an unresolved entity id — never the raw UUID (no-UUID rule). */
function maskEntityValue(fieldConfig: SimpleFieldConfig | undefined): string {
  if (fieldConfig && isUserFilterField(fieldConfig)) return "Selected user";
  if (fieldConfig && isCompanyFilterField(fieldConfig)) return "Selected company";
  if (fieldConfig && isContactFilterField(fieldConfig)) return "Selected contact";
  return "Selected";
}

/**
 * Shared helpers for rendering active filters and saved-view configurations as
 * human-readable text. Saved-view presets store filter keys with a `filter_`
 * prefix (e.g. `filter_stage`) while the table's field map is keyed without it
 * (e.g. `stage`); these helpers resolve that mismatch so labels never fall back
 * to the raw key.
 */

export type FieldMap = Readonly<Record<string, SimpleFieldConfig>>;

/** Resolve a field config by id, falling back to the `filter_`-stripped key. */
export function resolveFieldConfig(
  fieldId: string,
  fields: FieldMap
): SimpleFieldConfig | undefined {
  return fields[fieldId] ?? fields[fieldId.replace(/^filter_/, "")];
}

/** Human-readable field name; humanizes the key when no config exists. */
export function getFieldLabel(fieldId: string, fields: FieldMap): string {
  const fieldConfig = resolveFieldConfig(fieldId, fields);
  if (fieldConfig?.label) return fieldConfig.label;
  return fieldId
    .replace(/^filter_/, "")
    .replace(/_(min|max)$/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Map a single raw filter value to its display label (option label, preset, resolved name, Yes/No). */
function formatSingleValue(
  fieldConfig: SimpleFieldConfig | undefined,
  value: unknown,
  resolveName?: FilterNameResolver
): string {
  if (value === true || value === "true") return "Yes";
  if (value === false || value === "false") return "No";

  if (fieldConfig) {
    const options = fieldConfig.options ?? fieldConfig.filterOptions;
    if (Array.isArray(options)) {
      const match = options.find((opt) => opt.value === value);
      if (match) return match.label;
    }

    if (fieldConfig.datetimePresets?.enabled) {
      if (value === NEVER_LAST_ACTIVITY_PRESET_VALUE) return NEVER_LAST_ACTIVITY_PRESET_LABEL;
      const preset = getDatetimePresets().find((p) => p.value === value);
      if (preset) return preset.label;
    }
  }

  // Entity-reference ids: resolve to a name, or mask — never render a raw UUID
  // (the "Created By" simple-filter chip used to show the app_users UUID here).
  if (typeof value === "string") {
    const resolved = resolveName?.(fieldConfig, value);
    if (resolved) return resolved;
    if (looksLikeUuid(value)) return maskEntityValue(fieldConfig);
  }

  return String(value);
}

function isRangeValue(value: unknown): value is { min?: unknown; max?: unknown } {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    ("min" in value || "max" in value)
  );
}

export interface FilterValueParts {
  /** Individual readable values (expanded — never collapsed to "N selected"). */
  items: string[];
  /** Compact summary suitable for a chip face (e.g. "Lead" or "6 selected"). */
  summary: string;
}

/** Produce the chip summary plus the expanded item list for a filter value. */
export function getFilterValueParts(
  fieldId: string,
  value: unknown,
  fields: FieldMap,
  resolveName?: FilterNameResolver
): FilterValueParts {
  const fieldConfig = resolveFieldConfig(fieldId, fields);

  if (isRangeValue(value)) {
    const parts: string[] = [];
    if (value.min != null && value.min !== "") parts.push(`≥ ${value.min}`);
    if (value.max != null && value.max !== "") parts.push(`≤ ${value.max}`);
    const summary = parts.length > 0 ? parts.join(" and ") : "Any";
    return { items: parts.length > 0 ? parts : [summary], summary };
  }

  if (Array.isArray(value)) {
    const items = value.map((v) => formatSingleValue(fieldConfig, v, resolveName));
    const summary =
      items.length === 0 ? "Any" : items.length === 1 ? items[0] : `${items.length} selected`;
    return { items, summary };
  }

  const single = formatSingleValue(fieldConfig, value, resolveName);
  return { items: [single], summary: single };
}

function isEmptyFilterValue(value: unknown): boolean {
  return (
    value == null ||
    value === "" ||
    value === "all" ||
    (Array.isArray(value) && value.length === 0)
  );
}

export interface ActiveFilterEntry {
  /** The simple-filter key (used as-is for removal). */
  fieldId: string;
  label: string;
  parts: FilterValueParts;
}

/** Build display entries for the currently active simple filters. */
export function getActiveFilterEntries(
  simpleFilters: Record<string, unknown>,
  fields: FieldMap,
  resolveName?: FilterNameResolver
): ActiveFilterEntry[] {
  return Object.entries(simpleFilters)
    .filter(([, value]) => !isEmptyFilterValue(value))
    .map(([fieldId, value]) => ({
      fieldId,
      label: getFieldLabel(fieldId, fields),
      parts: getFilterValueParts(fieldId, value, fields, resolveName),
    }));
}

/** A single active advanced-filter condition, shaped for a toolbar chip. */
export interface ActiveAdvancedFilterEntry {
  /** The advanced-filter group id (used as-is for removal). */
  id: string;
  label: string;
  /** Human label for the operator/type (e.g. "At least", "Between"). */
  operatorLabel: string;
  parts: FilterValueParts;
}

/** The advanced-filter group shape as stored in FilterContext. */
interface AdvancedFilterGroupLike {
  id: string;
  field: string;
  operator: string;
  value: unknown;
}

/**
 * Build display entries for the active advanced filters so each condition can
 * render as a removable toolbar chip — mirroring the quick-filter chips. The
 * operator label is surfaced so "Amount At least 1000" reads correctly (an
 * advanced filter without it would look identical to a quick equality filter).
 */
export function getActiveAdvancedFilterEntries(
  advancedFilters: readonly AdvancedFilterGroupLike[],
  fields: FieldMap,
  resolveName?: FilterNameResolver
): ActiveAdvancedFilterEntry[] {
  return advancedFilters.map((filter) => ({
    id: filter.id,
    label: getFieldLabel(filter.field, fields),
    operatorLabel: getFilterTypeLabel(filter.operator),
    parts: getFilterValueParts(filter.field, filter.value, fields, resolveName),
  }));
}

export interface SavedViewSummary {
  filterEntries: Array<{ label: string; parts: FilterValueParts }>;
  search?: string;
  advancedCount: number;
  sort?: { label: string; direction: "asc" | "desc" };
  visibleColumnsCount?: number;
  /** True when there are no filters, search, advanced filters, or sort. */
  isEmpty: boolean;
}

/**
 * Summarize a saved view's configuration. Handles both the flat `TableFilters`
 * shape (config presets) and the nested `SerializedViewState` shape (stored
 * Supabase views with search/advancedFilters).
 */
export function getSavedViewSummary(
  view: SavedViewConfig,
  fields: FieldMap
): SavedViewSummary {
  const raw = (view.filters ?? {}) as Record<string, unknown>;
  const hasNested = typeof raw.filters === "object" && raw.filters !== null;

  const simpleFilters = (hasNested ? (raw.filters as Record<string, unknown>) : raw) ?? {};
  const search = hasNested && typeof raw.search === "string" ? raw.search : undefined;
  const advancedFilters = hasNested && Array.isArray(raw.advancedFilters) ? raw.advancedFilters : [];

  const filterEntries = getActiveFilterEntries(simpleFilters, fields).map(({ label, parts }) => ({
    label,
    parts,
  }));

  const sort = view.sort
    ? { label: getFieldLabel(view.sort.field, fields), direction: view.sort.direction }
    : undefined;

  const isEmpty =
    filterEntries.length === 0 && !search && advancedFilters.length === 0 && !sort;

  return {
    filterEntries,
    search: search || undefined,
    advancedCount: advancedFilters.length,
    sort,
    visibleColumnsCount: view.visibleColumns?.length,
    isEmpty,
  };
}
